import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { useApp } from '../context/AppContext';
import {
  parsePdfFile, parseBuyersSellers, parseTransactions,
  buyersSellersTobrokerData, transactionsToBrokerAndPrice,
  parseColBrokerData,
} from '../utils/pdfParser';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  FileText, Check, X, CalendarIcon,
  AlertTriangle, FileSearch, Loader2, ClipboardPaste,
} from 'lucide-react';

export default function PdfImport() {
  const { setBrokerData, setPriceData, setTransactionsData, brokerData, priceData, transactionsData } = useApp();
  const [importMode, setImportMode] = useState('pdf'); // 'pdf' or 'paste'
  const [stage, setStage] = useState('upload'); // upload | preview | done
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Parse results
  const [reportType, setReportType] = useState('COL_BROKER');
  const [symbol, setSymbol] = useState('');
  const [tradingDate, setTradingDate] = useState(null);
  const [parsedRecords, setParsedRecords] = useState([]);
  const [fileName, setFileName] = useState('');
  const [rawText, setRawText] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pdfExtractionFailed, setPdfExtractionFailed] = useState(false);

  // Extract symbol from filename: "Transactions (ACEN) - Philstocks2.pdf"
  const extractFromFilename = (name) => {
    const typeMatch = name.match(/^(Transactions?|Buyers?\s*(?:&|and)\s*Sellers?)/i);
    const symMatch = name.match(/\(([A-Z]{2,6})\)/i);
    return {
      type: typeMatch ? (typeMatch[1].toLowerCase().includes('transaction') ? 'TRANSACTIONS' : 'BUYERS_SELLERS') : null,
      symbol: symMatch ? symMatch[1].toUpperCase() : '',
    };
  };

  // Check if extracted text is garbled (non-readable)
  const isGarbledText = (text) => {
    if (!text || text.length < 50) return true;
    // Count readable vs non-readable characters
    const readable = text.match(/[a-zA-Z0-9:.,\s\-\/()]/g)?.length || 0;
    const ratio = readable / text.length;
    return ratio < 0.5; // If less than 50% readable, it's garbled
  };

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError('');
    setFileName(file.name);
    setPdfExtractionFailed(false);

    // Extract metadata from filename
    const fileInfo = extractFromFilename(file.name);
    if (fileInfo.symbol) setSymbol(fileInfo.symbol);
    if (fileInfo.type) setReportType(fileInfo.type);

    try {
      const result = await parsePdfFile(file);
      setRawText(result.rawText);

      // Check if text extraction produced readable content
      if (isGarbledText(result.rawText)) {
        setPdfExtractionFailed(true);
        setStage('preview');
        // Use filename-based detection
        if (!fileInfo.symbol && result.detectedSymbol) setSymbol(result.detectedSymbol);
        if (result.detectedDate) setTradingDate(new Date(result.detectedDate));
        setLoading(false);
        return;
      }

      // Normal parsing flow
      if (!fileInfo.type) setReportType(result.reportType);
      if (!fileInfo.symbol && result.detectedSymbol) setSymbol(result.detectedSymbol);
      if (result.detectedDate) setTradingDate(new Date(result.detectedDate));

      // Parse records
      const dateStr = result.detectedDate || format(new Date(), 'yyyy-MM-dd');
      const type = fileInfo.type || result.reportType;
      let records = [];
      if (type === 'BUYERS_SELLERS') {
        records = parseBuyersSellers(result.lines, fileInfo.symbol || result.detectedSymbol, dateStr);
      } else {
        records = parseTransactions(result.lines, fileInfo.symbol || result.detectedSymbol, dateStr);
      }
      setParsedRecords(records);
      setStage('preview');
    } catch (err) {
      setError(`Failed to parse PDF: ${err.message}`);
    } finally {
      setLoading(false);
    }
    e.target.value = '';
  }, []);

  // Parse pasted text
  const handlePasteConfirm = useCallback(() => {
    if (!pasteText.trim()) return;
    const lines = pasteText.split('\n').map(l => l.trim()).filter(Boolean);
    const dateStr = tradingDate ? format(tradingDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

    let records = [];
    if (reportType === 'COL_BROKER') {
      records = parseColBrokerData(lines, symbol, dateStr);
    } else if (reportType === 'BUYERS_SELLERS') {
      records = parseBuyersSellers(lines, symbol, dateStr);
    } else {
      records = parseTransactions(lines, symbol, dateStr);
    }
    setParsedRecords(records);
    if (records.length === 0) {
      const tsvLines = lines.map(l => l.replace(/\s{2,}/g, '\t'));
      if (reportType === 'TRANSACTIONS') records = parseTransactions(tsvLines, symbol, dateStr);
      else if (reportType === 'COL_BROKER') records = parseColBrokerData(tsvLines, symbol, dateStr);
      else records = parseBuyersSellers(tsvLines, symbol, dateStr);
      setParsedRecords(records);
    }
  }, [pasteText, reportType, symbol, tradingDate]);

  const handleConfirm = useCallback(() => {
    if (!parsedRecords.length) return;
    const dateStr = tradingDate ? format(tradingDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

    if (reportType === 'COL_BROKER') {
      // COL data is already in broker data format
      setBrokerData(prev => [...prev, ...parsedRecords]);
      // Also derive price data from the broker summary
      const totalValue = parsedRecords.reduce((s, r) => s + (r.totalValue || 0), 0);
      const totalVol = parsedRecords.reduce((s, r) => s + (r.buyVol || 0) + (r.sellVol || 0), 0);
      const allAves = parsedRecords.map(r => r.buyAve).concat(parsedRecords.map(r => r.sellAve)).filter(v => v > 0);
      if (allAves.length > 0) {
        const priceRow = {
          date: dateStr, symbol,
          open: allAves[0],
          high: Math.max(...allAves),
          low: Math.min(...allAves),
          close: allAves[allAves.length - 1],
          volume: totalVol / 2,
          value: totalValue / 2,
          _source: 'col',
        };
        setPriceData(prev => [...prev, priceRow]);
      }
    } else if (reportType === 'BUYERS_SELLERS') {
      const brokerRows = buyersSellersTobrokerData(parsedRecords, symbol, dateStr).map(r => ({ ...r, _source: 'pdf' }));
      setBrokerData(prev => [...prev, ...brokerRows]);
    } else {
      const txRecords = parsedRecords.map(r => ({ ...r, symbol, tradingDate: dateStr }));
      setTransactionsData(prev => [...prev, ...txRecords]);
      const { brokerRows, priceRows } = transactionsToBrokerAndPrice(txRecords);
      if (brokerRows.length > 0) setBrokerData(prev => [...prev, ...brokerRows]);
      if (priceRows.length > 0) setPriceData(prev => [...prev, ...priceRows]);
    }

    setStage('done');
    setTimeout(() => resetAll(), 2000);
  }, [parsedRecords, reportType, symbol, tradingDate, setBrokerData, setPriceData, setTransactionsData]);

  const resetAll = () => {
    setStage('upload');
    setReportType('TRANSACTIONS');
    setSymbol('');
    setTradingDate(null);
    setParsedRecords([]);
    setFileName('');
    setRawText('');
    setPasteText('');
    setError('');
    setPdfExtractionFailed(false);
  };

  const pdfBrokerRows = brokerData.filter(r => r._source === 'pdf').length;
  const txCount = transactionsData?.length || 0;

  return (
    <div className="space-y-5" data-testid="pdf-import-module">
      {/* Upload Stage */}
      {stage === 'upload' && (
        <>
          <Tabs value={importMode} onValueChange={setImportMode}>
            <TabsList className="bg-trading-elevated border border-trading-border mb-4">
              <TabsTrigger value="pdf" className="data-[state=active]:bg-trading-surface data-[state=active]:text-trading-text text-trading-muted text-sm">
                <FileSearch className="w-3.5 h-3.5 mr-1.5" /> Upload PDF
              </TabsTrigger>
              <TabsTrigger value="paste" className="data-[state=active]:bg-trading-surface data-[state=active]:text-trading-text text-trading-muted text-sm">
                <ClipboardPaste className="w-3.5 h-3.5 mr-1.5" /> Paste Data
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pdf">
              <label
                data-testid="pdf-dropzone"
                className="border-2 border-dashed border-trading-border hover:border-trading-blue/50 rounded-sm p-12 text-center cursor-pointer transition-colors duration-150 bg-trading-surface/50 block"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-8 h-8 text-trading-blue mx-auto mb-3 animate-spin" />
                    <p className="text-sm text-trading-muted">Parsing PDF...</p>
                  </>
                ) : (
                  <>
                    <FileSearch className="w-8 h-8 text-trading-muted mx-auto mb-3" />
                    <p className="text-sm text-trading-muted">Click to upload Philstocks PDF report</p>
                    <p className="text-xs text-trading-muted/60 mt-1">Supports Buyers & Sellers and Transactions PDFs</p>
                  </>
                )}
                <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" data-testid="pdf-upload-input" />
              </label>
            </TabsContent>

            <TabsContent value="paste">
              <PasteDataSection
                symbol={symbol}
                setSymbol={setSymbol}
                reportType={reportType}
                setReportType={setReportType}
                tradingDate={tradingDate}
                setTradingDate={setTradingDate}
                pasteText={pasteText}
                setPasteText={setPasteText}
                parsedRecords={parsedRecords}
                onParse={handlePasteConfirm}
                onConfirm={handleConfirm}
              />
            </TabsContent>
          </Tabs>
          {error && (
            <div className="flex items-center gap-2 text-trading-red text-sm bg-trading-red/5 border border-trading-red/20 p-3 rounded-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
        </>
      )}

      {/* Preview Stage (from PDF upload) */}
      {stage === 'preview' && (
        <div className="space-y-4">
          {/* File info */}
          <div className="flex items-center justify-between bg-trading-surface border border-trading-border p-3 rounded-sm">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-trading-amber" />
              <span className="text-sm font-mono text-trading-text">{fileName}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-sm bg-trading-amber/10 text-trading-amber border border-trading-amber/20 font-mono">PDF</span>
            </div>
            <Button variant="ghost" size="sm" onClick={resetAll} className="text-trading-muted hover:text-trading-text" data-testid="pdf-reset-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* PDF Extraction Failed — show paste fallback */}
          {pdfExtractionFailed && (
            <div className="bg-trading-amber/5 border border-trading-amber/20 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-trading-amber flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-trading-amber font-medium mb-1">PDF uses encoded fonts — text extraction limited</p>
                  <p className="text-xs text-trading-muted">
                    This Philstocks PDF uses custom font encoding. To import the data:
                  </p>
                  <ol className="text-xs text-trading-muted mt-2 space-y-1 list-decimal list-inside">
                    <li>Open the PDF in your PDF viewer or browser</li>
                    <li>Select All (Ctrl+A) and Copy (Ctrl+C)</li>
                    <li>Paste the text below</li>
                  </ol>
                </div>
              </div>
              <div className="mt-4">
                <textarea
                  data-testid="pdf-paste-fallback"
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  placeholder="Paste the copied text from your PDF viewer here..."
                  className="w-full h-48 bg-trading-bg border border-trading-border rounded-sm p-3 text-xs font-mono text-trading-text resize-none focus:border-trading-blue focus:ring-1 focus:ring-trading-blue/50"
                />
                <Button
                  onClick={handlePasteConfirm}
                  disabled={!pasteText.trim()}
                  className="mt-2 bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm text-sm"
                  data-testid="parse-pasted-btn"
                >
                  Parse Pasted Text
                </Button>
              </div>
            </div>
          )}

          {/* Configuration */}
          <div className="bg-trading-surface border border-trading-border rounded-sm p-4">
            <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-4">Import Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Report Type</label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger data-testid="pdf-report-type" className="h-8 bg-trading-bg border-trading-border text-sm text-trading-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-trading-surface border-trading-border z-[100]">
                    <SelectItem value="BUYERS_SELLERS" className="text-sm text-trading-text hover:bg-trading-elevated">Buyers & Sellers</SelectItem>
                    <SelectItem value="TRANSACTIONS" className="text-sm text-trading-text hover:bg-trading-elevated">Transactions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Symbol <span className="text-trading-red">*</span></label>
                <Input
                  data-testid="pdf-symbol-input"
                  value={symbol}
                  onChange={e => setSymbol(e.target.value.toUpperCase())}
                  placeholder="e.g. ACEN"
                  className="h-8 bg-trading-bg border-trading-border text-sm text-trading-text uppercase"
                />
                {symbol && <span className="text-[10px] text-trading-green mt-1 block">From filename: {symbol}</span>}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Trading Date <span className="text-trading-red">*</span></label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      data-testid="pdf-date-picker"
                      className="w-full h-8 justify-start text-left font-mono text-sm bg-trading-bg border border-trading-border text-trading-text hover:bg-trading-elevated rounded-sm"
                    >
                      <CalendarIcon className="w-3.5 h-3.5 mr-2 text-trading-muted" />
                      {tradingDate ? format(tradingDate, 'yyyy-MM-dd') : 'Select date...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-trading-surface border-trading-border z-[100]" align="start">
                    <Calendar mode="single" selected={tradingDate} onSelect={setTradingDate} initialFocus className="bg-trading-surface text-trading-text" />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Records Preview */}
          {parsedRecords.length > 0 && (
            <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">
                  Extracted Records ({parsedRecords.length})
                </span>
                <span className="text-[10px] font-mono text-trading-green">Ready to import</span>
              </div>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                {reportType === 'TRANSACTIONS' ? (
                  <TransactionsPreview records={parsedRecords} />
                ) : reportType === 'COL_BROKER' ? (
                  <ColBrokerPreview records={parsedRecords} />
                ) : (
                  <BuyersSellersPreview records={parsedRecords} />
                )}
              </div>
            </div>
          )}

          {/* Raw text toggle */}
          {rawText && <RawTextPreview text={rawText} />}

          {/* Confirm */}
          <div className="flex gap-3">
            <Button
              onClick={handleConfirm}
              disabled={!parsedRecords.length || !symbol || !tradingDate}
              data-testid="pdf-confirm-btn"
              className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm"
            >
              <Check className="w-4 h-4 mr-2" /> Import {parsedRecords.length} records
            </Button>
            <Button variant="ghost" onClick={resetAll} className="text-trading-muted hover:text-trading-text border border-trading-border rounded-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Done */}
      {stage === 'done' && (
        <div className="bg-trading-green/5 border border-trading-green/20 rounded-sm p-6 text-center">
          <Check className="w-8 h-8 text-trading-green mx-auto mb-2" />
          <p className="text-sm text-trading-green font-medium">Data imported successfully!</p>
          <p className="text-xs text-trading-muted mt-1">Broker flow and price data derived. Check Signals for recommendations.</p>
        </div>
      )}

      {/* Data Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <PdfDataCard title="PDF Broker Flow Data" count={brokerData.length} pdfCount={pdfBrokerRows} testId="pdf-broker-summary" />
        <PdfDataCard title="Transactions Data" count={txCount} testId="pdf-transactions-summary" />
      </div>
    </div>
  );
}

function PasteDataSection({ symbol, setSymbol, reportType, setReportType, tradingDate, setTradingDate, pasteText, setPasteText, parsedRecords, onParse, onConfirm }) {
  return (
    <div className="space-y-4">
      <div className="bg-trading-surface border border-trading-border rounded-sm p-4">
        <h3 className="text-xs uppercase tracking-widest text-trading-muted font-semibold mb-3">Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Report Type</label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="h-8 bg-trading-bg border-trading-border text-sm text-trading-text">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-trading-surface border-trading-border z-[100]">
                <SelectItem value="COL_BROKER" className="text-sm">COL Broker Summary</SelectItem>
                <SelectItem value="TRANSACTIONS" className="text-sm">Transactions (Philstocks)</SelectItem>
                <SelectItem value="BUYERS_SELLERS" className="text-sm">Buyers & Sellers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Symbol <span className="text-trading-red">*</span></label>
            <Input value={symbol} onChange={e => setSymbol(e.target.value.toUpperCase())} placeholder="e.g. ACEN" className="h-8 bg-trading-bg border-trading-border text-sm text-trading-text uppercase" data-testid="paste-symbol-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">Trading Date <span className="text-trading-red">*</span></label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" className="w-full h-8 justify-start text-left font-mono text-sm bg-trading-bg border border-trading-border text-trading-text hover:bg-trading-elevated rounded-sm" data-testid="paste-date-picker">
                  <CalendarIcon className="w-3.5 h-3.5 mr-2 text-trading-muted" />
                  {tradingDate ? format(tradingDate, 'yyyy-MM-dd') : 'Select date...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-trading-surface border-trading-border z-[100]" align="start">
                <Calendar mode="single" selected={tradingDate} onSelect={setTradingDate} initialFocus className="bg-trading-surface text-trading-text" />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <label className="text-[10px] uppercase tracking-widest text-trading-muted font-semibold mb-1 block">
          {reportType === 'COL_BROKER' ? 'Paste COL Broker Data' : 'Paste Transaction Data'}
        </label>
        <textarea
          data-testid="paste-data-textarea"
          value={pasteText}
          onChange={e => setPasteText(e.target.value)}
          placeholder={reportType === 'COL_BROKER'
            ? "Paste from COL Historical Broker Transactions table:\n1\t203\tCOL FINANCIAL GROUP\t55,662,000\t158,934,400\t2.8553\t53,614,000\t155,599,990\t2.9022\t3,334,410\t314,534,390\n2\t267\tFIRST METRO SECURITIES\t17,860,000\t51,254,790\t2.8698\t24,871,000\t72,459,030\t2.9134\t-21,204,240\t123,713,820"
            : "Paste your data here. Example format:\n11:53:20 AM\t2.81\t11,000\tsbequiti\tcol\n11:53:20 AM\t2.81\t7,000\tphlstcks\tcol\n..."
          }
          className="w-full h-56 bg-trading-bg border border-trading-border rounded-sm p-3 text-xs font-mono text-trading-text resize-none focus:border-trading-blue focus:ring-1 focus:ring-trading-blue/50"
        />
        <div className="flex gap-3 mt-3">
          <Button onClick={onParse} disabled={!pasteText.trim() || !symbol} className="bg-trading-blue hover:bg-trading-blue/90 text-white rounded-sm text-sm" data-testid="parse-paste-btn">
            Parse Data ({pasteText.split('\n').filter(l => l.trim()).length} lines)
          </Button>
          {parsedRecords.length > 0 && (
            <Button onClick={onConfirm} disabled={!tradingDate} className="bg-trading-green hover:bg-trading-green/90 text-white rounded-sm text-sm" data-testid="confirm-paste-btn">
              <Check className="w-4 h-4 mr-2" /> Import {parsedRecords.length} records
            </Button>
          )}
        </div>
      </div>

      {parsedRecords.length > 0 && (
        <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
          <div className="px-4 py-2 border-b border-trading-border bg-trading-elevated">
            <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">Parsed: {parsedRecords.length} records</span>
          </div>
          <div className="overflow-x-auto max-h-[250px] overflow-y-auto">
            {reportType === 'COL_BROKER' ? (
              <ColBrokerPreview records={parsedRecords} />
            ) : reportType === 'TRANSACTIONS' ? (
              <TransactionsPreview records={parsedRecords} />
            ) : (
              <BuyersSellersPreview records={parsedRecords} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TransactionsPreview({ records }) {
  return (
    <table className="w-full data-table" data-testid="tx-preview-table">
      <thead>
        <tr className="bg-trading-elevated border-b border-trading-border">
          <th>Time</th><th>Price</th><th>Volume</th><th>Buyer</th><th>Seller</th>
        </tr>
      </thead>
      <tbody>
        {records.slice(0, 50).map((r, i) => (
          <tr key={i} className="border-b border-trading-border/50 hover:bg-trading-elevated/30">
            <td className="font-mono text-trading-muted">{r.time}</td>
            <td className="font-mono text-trading-text">{r.price?.toFixed(4)}</td>
            <td className="font-mono">{r.volume?.toLocaleString()}</td>
            <td className="text-trading-green text-xs">{r.buyer}</td>
            <td className="text-trading-red text-xs">{r.seller}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ColBrokerPreview({ records }) {
  return (
    <table className="w-full data-table" data-testid="col-preview-table">
      <thead>
        <tr className="bg-trading-elevated border-b border-trading-border">
          <th>Broker</th><th>Buy Vol</th><th>Buy Amt</th><th>Buy Ave</th><th>Sell Vol</th><th>Sell Amt</th><th>Sell Ave</th><th>Net Shares</th><th>Total Value</th>
        </tr>
      </thead>
      <tbody>
        {records.slice(0, 40).map((r, i) => (
          <tr key={i} className="border-b border-trading-border/50 hover:bg-trading-elevated/30">
            <td className="text-trading-text text-xs">{r.broker}</td>
            <td className="font-mono text-trading-green text-xs">{r.buyVol?.toLocaleString()}</td>
            <td className="font-mono text-xs">{r.buyAmt?.toLocaleString()}</td>
            <td className="font-mono text-xs">{r.buyAve?.toFixed(4)}</td>
            <td className="font-mono text-trading-red text-xs">{r.sellVol?.toLocaleString()}</td>
            <td className="font-mono text-xs">{r.sellAmt?.toLocaleString()}</td>
            <td className="font-mono text-xs">{r.sellAve?.toFixed(4)}</td>
            <td className={`font-mono text-xs ${r.netShares >= 0 ? 'text-trading-green' : 'text-trading-red'}`}>
              {r.netShares >= 0 ? '+' : ''}{r.netShares?.toLocaleString()}
            </td>
            <td className="font-mono text-xs">{r.totalValue?.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BuyersSellersPreview({ records }) {
  return (
    <table className="w-full data-table" data-testid="bs-preview-table">
      <thead>
        <tr className="bg-trading-elevated border-b border-trading-border">
          <th>Broker</th><th>Side</th><th>Volume</th><th>Average</th><th>Vol %</th>
        </tr>
      </thead>
      <tbody>
        {records.slice(0, 30).map((r, i) => (
          <tr key={i} className="border-b border-trading-border/50 hover:bg-trading-elevated/30">
            <td className="text-trading-text">{r.broker}</td>
            <td><span className={`text-[11px] font-mono ${r.side === 'BUYER' ? 'text-trading-green' : 'text-trading-red'}`}>{r.side}</span></td>
            <td className="font-mono">{r.volume?.toLocaleString()}</td>
            <td className="font-mono">{r.average?.toFixed(4)}</td>
            <td className="font-mono text-trading-muted">{r.volumePercent ? `${r.volumePercent}%` : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RawTextPreview({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  return (
    <div className="bg-trading-surface border border-trading-border rounded-sm overflow-hidden">
      <button onClick={() => setExpanded(v => !v)} className="w-full px-4 py-2 border-b border-trading-border bg-trading-elevated flex items-center justify-between text-left" data-testid="raw-text-toggle">
        <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold">Raw PDF Text</span>
        <span className="text-xs text-trading-muted">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      {expanded && (
        <pre className="p-3 text-[11px] font-mono text-trading-muted/80 overflow-x-auto max-h-[200px] overflow-y-auto whitespace-pre-wrap" data-testid="raw-text-content">{text}</pre>
      )}
    </div>
  );
}

function PdfDataCard({ title, count, pdfCount, testId }) {
  return (
    <div data-testid={testId} className="bg-trading-surface border border-trading-border rounded-sm p-4">
      <span className="text-xs uppercase tracking-widest text-trading-muted font-semibold block mb-2">{title}</span>
      <div className="flex gap-6">
        <div>
          <span className="font-mono text-xl text-trading-text tabular-nums">{count.toLocaleString()}</span>
          <span className="text-xs text-trading-muted ml-1">total</span>
        </div>
        {pdfCount !== undefined && (
          <div>
            <span className="font-mono text-xl text-trading-amber tabular-nums">{pdfCount.toLocaleString()}</span>
            <span className="text-xs text-trading-muted ml-1">from PDF</span>
          </div>
        )}
      </div>
    </div>
  );
}
